/**
 * Store Pinia : chantiers — Phase 6 CP-1.
 *
 * Pattern reutilisable pour les autres domaines (pointages, fiches, journal,
 * livraisons, demandes). Resume :
 *   - state.byId : Record<number, Chantier> (cache local)
 *   - state.allIds : number[] (ordre serveur)
 *   - actions fetchList / fetchOne / create / update / archive
 *   - getters list (Chantier[] derive de byId + allIds) + activeList
 *
 * Strategie cache : on garde tout en memoire pendant la session. Pas de
 * stockage local persistant (les chantiers changent souvent, mieux vaut
 * refetch a chaque entree sur la vue liste).
 */

import { defineStore } from 'pinia';
import { chantiersApi } from '../api/endpoints';
import type { Chantier, ChantierCreateInput, ChantierUpdateInput } from '../types/models';

interface ChantiersState {
  byId: Record<number, Chantier>;
  allIds: number[];
  loading: boolean;
  error: string | null;
}

export const useChantiersStore = defineStore('chantiers', {
  state: (): ChantiersState => ({
    byId: {},
    allIds: [],
    loading: false,
    error: null,
  }),

  getters: {
    list: (state): Chantier[] => state.allIds.map((id) => state.byId[id]).filter(Boolean),
    activeList(): Chantier[] {
      return this.list.filter((c) => c.status === 'active' && !c.archived_at);
    },
    archivedList(): Chantier[] {
      return this.list.filter((c) => c.status === 'archived' || c.archived_at !== null);
    },
    findById: (state) => (id: number): Chantier | null => state.byId[id] ?? null,
  },

  actions: {
    async fetchList(): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        const { chantiers } = await chantiersApi.list();
        this.byId = {};
        this.allIds = [];
        for (const c of chantiers) {
          this.byId[c.id] = c;
          this.allIds.push(c.id);
        }
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'fetch_failed';
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async fetchOne(id: number): Promise<Chantier> {
      this.loading = true;
      this.error = null;
      try {
        const { chantier } = await chantiersApi.get(id);
        this.byId[chantier.id] = chantier;
        if (!this.allIds.includes(chantier.id)) {
          this.allIds.push(chantier.id);
        }
        return chantier;
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'fetch_failed';
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async create(input: ChantierCreateInput): Promise<number> {
      const { id } = await chantiersApi.create(input);
      await this.fetchOne(id);
      return id;
    },

    async update(id: number, patch: ChantierUpdateInput): Promise<void> {
      await chantiersApi.update(id, patch);
      await this.fetchOne(id);
    },

    async archive(id: number): Promise<void> {
      await chantiersApi.archive(id);
      const c = this.byId[id];
      if (c) {
        c.status = 'archived';
        c.archived_at = new Date().toISOString();
      }
    },

    reset(): void {
      this.byId = {};
      this.allIds = [];
      this.error = null;
      this.loading = false;
    },
  },
});
